import React, { useEffect, useState } from "react";
import api, { photoUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Image as ImageIcon, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

const empty = {
  name: "",
  website: "",
  logo_url: "",
  logo_photo_id: null,
  contact_info: "",
  pricing_emails: [],
  marketing_reply_to: "",
  marketing_signature: "",
};

export default function AgencyFormDialog({
  open,
  onOpenChange,
  mode = "create",
  agency,
  onSaved,
}) {
  const [form, setForm] = useState(empty);
  const [logoFile, setLogoFile] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && agency) {
      // Seed from the list-cached object first so the form isn't blank.
      setForm({
        name: agency.name || "",
        website: agency.website || "",
        logo_url: agency.logo_url || "",
        logo_photo_id: agency.logo_photo_id || null,
        contact_info: agency.contact_info || "",
        pricing_emails: agency.pricing_emails || [],
        marketing_reply_to: agency.marketing_reply_to || "",
        marketing_signature: agency.marketing_signature || "",
      });
      // Immediately refetch the authoritative doc so a "ghost" save from an
      // earlier deploy (where the backend silently dropped an unknown field)
      // can't leave the form showing a value that isn't in the DB.
      api
        .get(`/rep-agencies/${agency.id}`)
        .then(({ data }) => {
          setForm((prev) => ({
            ...prev,
            name: data.name || prev.name,
            website: data.website || "",
            logo_url: data.logo_url || "",
            logo_photo_id: data.logo_photo_id || null,
            contact_info: data.contact_info || "",
            pricing_emails: data.pricing_emails || [],
            marketing_reply_to: data.marketing_reply_to || "",
            marketing_signature: data.marketing_signature || "",
          }));
        })
        .catch(() => {});
    } else {
      setForm(empty);
    }
    setLogoFile(null);
    setEmailInput("");
  }, [open, mode, agency]);

  const removeLogo = () => {
    setForm({ ...form, logo_photo_id: null });
    setLogoFile(null);
  };

  const logoPreviewSrc = logoFile
    ? URL.createObjectURL(logoFile)
    : form.logo_photo_id
    ? photoUrl(form.logo_photo_id)
    : null;

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (form.pricing_emails.includes(e)) {
      toast.error("Email already added");
      return;
    }
    setForm({ ...form, pricing_emails: [...form.pricing_emails, e] });
    setEmailInput("");
  };

  const removeEmail = (email) => {
    setForm({
      ...form,
      pricing_emails: form.pricing_emails.filter((e) => e !== email),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      let logo_photo_id = form.logo_photo_id;
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const up = await api.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        logo_photo_id = up.data.id;
      }
      const payload = { ...form, logo_photo_id: logo_photo_id || "" };
      if (mode === "edit" && agency) {
        await api.patch(`/rep-agencies/${agency.id}`, payload);
        toast.success("Agency updated");
      } else {
        await api.post("/rep-agencies", payload);
        toast.success("Agency created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="agency-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {mode === "edit" ? "Edit rep agency" : "New rep agency"}
          </DialogTitle>
          <DialogDescription>
            Configure branding, contact, and pricing-contact emails. These
            settings are surfaced to distributors when they search this
            agency&apos;s portfolio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Agency name *
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Acme Rep Agency"
              className="mt-1 rounded-sm"
              data-testid="agency-name-input"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                Website
              </Label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://…"
                className="mt-1 rounded-sm"
                data-testid="agency-website-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                Logo URL (optional)
              </Label>
              <Input
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://…/logo.png"
                className="mt-1 rounded-sm"
                data-testid="agency-logo-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Agency logo (upload)
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Shown to this agency&apos;s users across the app after they log in.
              Uploaded image takes priority over the Logo URL.
            </p>
            <div className="mt-2 flex items-center gap-3">
              {logoPreviewSrc && (
                <div className="relative shrink-0">
                  <img
                    src={logoPreviewSrc}
                    alt="Logo preview"
                    className="w-14 h-14 object-contain border border-border rounded-sm bg-slate-50"
                    data-testid="agency-logo-preview"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-slate-900 text-white rounded-sm flex items-center justify-center"
                    aria-label="Remove logo"
                    data-testid="agency-logo-remove-btn"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-sm text-sm cursor-pointer hover:bg-slate-50">
                <ImageIcon className="w-4 h-4" />
                {logoPreviewSrc ? "Change" : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  data-testid="agency-logo-upload-input"
                />
              </label>
              {logoFile && (
                <span className="text-xs text-muted-foreground truncate">
                  {logoFile.name}
                </span>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Contact info
            </Label>
            <Textarea
              value={form.contact_info}
              onChange={(e) => setForm({ ...form, contact_info: e.target.value })}
              placeholder="Phone, mailing address, hours…"
              className="mt-1 rounded-sm min-h-[64px]"
              data-testid="agency-contact-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Pricing contact emails
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Shown to distributors on search results. Add one or more.
            </p>
            <div className="mt-2 flex gap-2">
              <Input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addEmail();
                  }
                }}
                placeholder="quotes@example.com"
                className="rounded-sm"
                data-testid="agency-email-input"
              />
              <Button
                type="button"
                onClick={addEmail}
                variant="outline"
                className="rounded-sm shrink-0"
                data-testid="agency-add-email-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.pricing_emails.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.pricing_emails.map((e) => (
                  <span
                    key={e}
                    className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-2 py-1 rounded-sm"
                    data-testid={`pricing-email-chip-${e}`}
                  >
                    {e}
                    <button
                      type="button"
                      onClick={() => removeEmail(e)}
                      className="text-amber-700 hover:text-amber-900"
                      aria-label={`Remove ${e}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Marketing reply-to
            </Label>
            <Input
              type="email"
              value={form.marketing_reply_to}
              onChange={(e) =>
                setForm({ ...form, marketing_reply_to: e.target.value })
              }
              placeholder="wayne@byrdrepagency.com"
              className="rounded-sm mt-1"
              data-testid="agency-marketing-reply-to-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              When a distributor hits <em>Reply</em> on a marketing email from
              this agency, their message goes here. Leave blank and replies fall
              back to <code>noreply@myrepagency.com</code> (a black hole — not
              recommended).
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
              Marketing signature
            </Label>
            <Textarea
              value={form.marketing_signature}
              onChange={(e) =>
                setForm({ ...form, marketing_signature: e.target.value })
              }
              placeholder={"Thank you,\n\nWayne Byrd\nByrd Rep Agency\nP: 832-813-9802\nE: wayne@byrdrepagency.com"}
              className="rounded-sm mt-1 font-mono text-[13px] min-h-[120px]"
              data-testid="agency-marketing-signature-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              Auto-appended to every marketing email from this agency. Line
              breaks are preserved. Recipients see this right above the
              unsubscribe footer.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="agency-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === "edit" ? "Save changes" : "Create agency"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
